import { Card, Row, Col, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBullseye,
    faBellSlash,
    faBedPulse,
    faMobileScreenButton,
    faGlobe,
    faBoltLightning,
    faLink,
    faSackDollar,
} from "@fortawesome/free-solid-svg-icons";
import { faCircleQuestion } from "@fortawesome/free-regular-svg-icons";
import formatMoney from "../../../utils/formatMoney";

// Thẻ số của trang growth app — lấy từ DÒNG CHỤP MỚI NHẤT, không phải cộng dồn.
//
// Vì sao không cộng dồn: mấy chỉ số này là "đếm người DISTINCT" (NSM 7 ngày, tệp
// ngủ đông, người không push được). Cộng số của nhiều ngày lại là đếm trùng người.
//
// Ô nào không đo được thì hiện "—", KHÔNG hiện 0: nhóm "sức khoẻ tệp" chỉ chụp
// được tại thời điểm cron chạy, ngày backfill quá khứ để 0 một cách cố ý (xem
// backend/src/models/AppGrowthStat.js). Hiện 0 sẽ đọc thành "không còn ai".

const num = (v) => (Number(v) || 0).toLocaleString("vi-VN");
const money = (v) => formatMoney.format(Number(v) || 0);

/**
 * Lợi nhuận gộp kênh app = HH sàn − phần trả user − phần trả người giới thiệu.
 * Tính ở đây thay vì lưu thành cột DB để không có hai nguồn sự thật lệch nhau.
 * `Orders` không có cột giá trị đơn (GMV) nên "doanh số" của LH chính là HH sàn.
 */
function grossProfitApp(row) {
    if (!row) return null;
    return (
        (Number(row.commission_app) || 0)
        - (Number(row.user_commission_app) || 0)
        - (Number(row.ref_commission_app) || 0)
    );
}

// 3 mục tiêu chốt 30/09/2026 nằm ở 3 thẻ đầu: người DÙNG app · người GẮN LINK ·
// doanh số + lợi nhuận. Các thẻ sau là tệp cần tác động để ba số đó tăng.
const CARDS = [
    {
        key: "app_users_open_30d",
        title: "Người dùng app (30 ngày)",
        icon: faMobileScreenButton,
        iconColor: "text-indigo-600",
        bgColor: "bg-indigo-50",
        subtitle: "Có mở app trong 30 ngày",
        help: "Suy từ PushSubscriptions.lastSeen. Chỉ đo được dạng ảnh chụp — lastSeen bị ghi đè mỗi lần mở app nên không dựng lại được số theo từng ngày. Muốn xem theo ngày thì nhìn 'Người gắn link'.",
    },
    {
        key: "link_users_app",
        title: "Người gắn link / ngày",
        icon: faLink,
        iconColor: "text-cyan-600",
        bgColor: "bg-cyan-50",
        subtitle: "Dán link sản phẩm vào app trong ngày",
        help: "Bậc thang ngay trước đơn. Tỷ lệ gắn-link → ra-đơn đã ~84% (W39: 5.522/6.563), nên muốn tăng đơn phải tăng SỐ NGƯỜI GẮN LINK, không phải tăng tỷ lệ chuyển đổi.",
    },
    {
        key: "gross_profit_app",
        title: "Lợi nhuận gộp app (ngày)",
        icon: faSackDollar,
        iconColor: "text-emerald-600",
        bgColor: "bg-emerald-50",
        isMoney: true,
        subtitle: "HH sàn − trả user − trả người giới thiệu",
        help: "Orders KHÔNG có cột giá trị đơn (GMV) nên 'doanh số' ở đây là hoa hồng sàn trả cho LH, không phải tổng tiền user mua. Số của ngày gần đây CÒN TĂNG vì sàn duyệt đơn dần.",
    },
    {
        key: "nsm_7d",
        title: "North Star — NSM 7 ngày",
        icon: faBullseye,
        iconColor: "text-blue-600",
        bgColor: "bg-blue-50",
        subtitle: "Người có ≥1 đơn qua app trong 7 ngày",
        help: "Cửa sổ trượt 7 ngày, không phải tuần lịch — tuần lịch luôn để lại một tuần cụt trông như sụt giảm.",
    },
    {
        key: "push_gap_buyers",
        title: "Mua app nhưng không push được",
        icon: faBellSlash,
        iconColor: "text-red-600",
        bgColor: "bg-red-50",
        subtitle: "Có đơn app 30 ngày, không còn token nhận noti",
        help: "Tệp đòn bẩy rẻ nhất: họ đã mua rồi, chỉ cần gọi được là quay lại. Mốc 29/09/2026: 1.167 người.",
    },
    {
        key: "app_users_dormant",
        title: "Ngủ đông",
        icon: faBedPulse,
        iconColor: "text-orange-500",
        bgColor: "bg-orange-50",
        subtitle: "Từng mua qua app trong 90 ngày, 30 ngày im",
        help: "Biết cách dùng app rồi nên win-back rẻ hơn kích hoạt người mới.",
    },
    {
        key: "app_users_idle_90d",
        title: "Có app, 90 ngày không đơn",
        icon: faMobileScreenButton,
        iconColor: "text-slate-500",
        bgColor: "bg-slate-100",
        subtitle: "Tệp lớn nhất nhưng lạnh nhất",
        help: "Gồm cả tài khoản rác (vụ 82k tài khoản đăng ký spam 07/2026) — đừng coi hết là người thật.",
    },
    {
        key: "web_only_users_30d",
        title: "Còn lại trên web",
        icon: faGlobe,
        iconColor: "text-purple-600",
        bgColor: "bg-purple-50",
        subtitle: "30 ngày chỉ mua qua web, chưa hề mua qua app",
        help: "Trần của đòn bẩy 'kéo web sang app'. Mốc 29/09/2026 chỉ còn 553 người — đừng dựng sprint quanh nó.",
    },
    {
        key: "push_reachable",
        title: "Trần liên lạc",
        icon: faBoltLightning,
        iconColor: "text-green-600",
        bgColor: "bg-green-50",
        subtitle: "Người còn bật nhận thông báo",
        help: "Mọi chiến dịch push không thể vượt số này, dù tệp có app lớn hơn.",
    },
];

function AppGrowthSummaryCards({ latest, loading }) {
    return (
        <Row gutter={[16, 16]}>
            {CARDS.map((c) => {
                // gross_profit_app không phải cột DB — tính từ 3 cột tiền.
                const raw = c.key === "gross_profit_app"
                    ? grossProfitApp(latest)
                    : (latest ? latest[c.key] : null);
                const measured = Number(raw) > 0;
                const fmt = c.isMoney ? money : num;
                return (
                    <Col xs={24} sm={12} lg={8} key={c.key}>
                        <Card className="shadow-sm h-full" loading={loading}>
                            <div className="flex items-start gap-3">
                                <div className={`${c.bgColor} rounded-lg p-3 shrink-0`}>
                                    <FontAwesomeIcon icon={c.icon} className={c.iconColor} />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-xs text-gray-500 flex items-center gap-1">
                                        <span className="truncate">{c.title}</span>
                                        <Tooltip title={c.help}>
                                            <FontAwesomeIcon
                                                icon={faCircleQuestion}
                                                className="text-gray-400 cursor-help"
                                            />
                                        </Tooltip>
                                    </div>
                                    <div className="text-2xl font-semibold leading-tight">
                                        {measured ? fmt(raw) : "—"}
                                    </div>
                                    <div className="text-xs text-gray-400">{c.subtitle}</div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                );
            })}
        </Row>
    );
}

export default AppGrowthSummaryCards;
